import { notFound } from "@tanstack/react-router";
import { buildHref, matchRoute } from "@softmax/web-addresses";
import { curatedLeagueName, curatedText } from "@softmax/web-features/coworlds";
import { leagueRoute, watchAddresses } from "@softmax/web-features/leagues";
import type { GamePageContext } from "./game-types";
import {
  theaterCorpus,
  watchLeagueFor,
  watchSeed,
} from "@/features/watch/data";

import { leagueTag } from "@/lib/leagues/watch";
import { playerNotPolicy, socialDescription } from "./social/social-card";
import type { LeagueSummary } from "@softmax/observatory-client/types";

export async function readGameTheater({
  signal,
  href,
  game,
}: {
  signal?: AbortSignal | undefined;
  href: string;
  game: GamePageContext;
}) {
  const route = matchRoute(new URL(href, window.location.href).pathname);

  if (route.name !== "game.home" && route.name !== "game.league")
    throw notFound();

  const address = [game.address.slug, route.league].filter(Boolean).join("/");
  const { leagues, viewerOnly, viewer } = await theaterCorpus({
    signal,
    slug: address,
    context: game,
  });

  if (leagues && !watchAddresses(leagues).leagueOf(address)) throw notFound();

  const leagueId = new URL(href, window.location.href).searchParams.get(
    "league",
  );
  const selection = { corpus: leagues, slug: address, leagueId, viewer };
  const [seed, league] = await Promise.all([
    watchSeed({ signal, address: selection, withState: false }),
    watchLeagueFor({ signal, address: selection }),
  ]);
  const canonical = league
    ? buildHref({ name: "game.league", ...leagueRoute(league) })
    : buildHref({ name: "game.home", game: game.address });
  const title = league
    ? `${curatedLeagueName(league)} · Softmax`
    : "Watch · Softmax";
  const description = theaterDescription(league);
  return {
    viewer,
    seed,
    title,
    description,
    socialDescription: league ? socialDescription(league) : description,
    canonical,
    markdown: league
      ? buildHref({ name: "game.playMarkdown", ...leagueRoute(league) })
      : null,
    indexable: !viewerOnly && game.address.catalog_status !== "unlisted",
  };
}

function theaterDescription(league: LeagueSummary | null): string {
  if (!league) return "Watch league matches on Softmax.";

  const description = league.game.description?.trim();

  if (description) return playerNotPolicy(curatedText(description));

  return `Watch the top two players in ${curatedLeagueName(league)} play ${leagueTag(league)}.`;
}
